import withParams from "@src/components/widgets/HOC/withParams";
import React, { useMemo } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetHighlighProductParams } from "../interface";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import LandingHighlighProductMargauxMotin from "design-system/src/Components/WidgetPart/LandingHighlighProduct/LandingHighlighProductMargauxMotin/LandingHighlighProductMargauxMotin";
import useCompositionBox from "@src/components/widgets/hooks/useCompositionBox";
import htmlParse from "html-react-parser";
import { useTranslation } from "@src/hooks/useTranslation";
import type { ICompositionVariant } from "design-system/src/Components/WidgetPart/LandingHighlighProduct/LandingHighlighProduct";
import { usePushAppsModal } from "@src/lib/WidgetPushApp/WidgetPushApp";
import useCartModelChange from "@src/backbone/hooks/useCartModelChange";

const HighlightProductMargauxMotin: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetHighlighProductParams>((props) => {
    const { p } = props;
    const cta_action = p("cta_action");
    const isAddToCart: boolean = cta_action === "add_to_cart";
    const id_product = p("product") || p("id_product");
    const ctaLabel = p("cta_label") || "";
    const mediaSource = p("media_source") || "image";
    const attachPushApps: boolean = p("attach_to_push_apps") === "1";
    const hideCta = p("hide_cta") === "1";
    const { t } = useTranslation("Product");
    const [cartData, cart] = useCartModelChange(["id_store"]);

    const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");
    const { prepareCompositions, prepareProduct } = useCompositionBox();
    const { openModal, canShowPushApps } = usePushAppsModal();

    const productModel = prepareProduct(p("id_product") || "");

    const onClick = generateClick(
      cta_action && cta_action === "open_link"
        ? {
            type: cta_action,
            link: p("link") || "#",
          }
        : {
            type: cta_action || "product_card",
          }
    );

    const handleClick = () => {
      if (attachPushApps && canShowPushApps()) {
        openModal();
      } else {
        onClick();
      }
    };

    const compositions: ICompositionVariant | undefined = useMemo(() => {
      const source = p("composition_source");
      if (source === "box") {
        const productIds: string[] = (p("box_products") || "")
          .split(",")
          .map((id) => id.trim());
        const productQuantity: number[] = (p("box_products_itemnbr") || "")
          .split(",")
          .map((i) => +i);
        const newProductIds: string[] = (p("box_products_new") || "")
          .split(",")
          .map((id) => id.trim());
        return {
          type: "box",
          list:
            prepareCompositions(productIds, productQuantity, newProductIds) ||
            [],
        } as ICompositionVariant;
      } else if (source === "paragraph") {
        return {
          type: "paragraph",
          text: htmlParse(p("composition_paragraph") || ""),
        } as ICompositionVariant;
      }
      return undefined;
    }, [p, prepareCompositions]);

    const displayFromMention =
      !cartData.id_store || cartData.id_store === "0"
        ? t("À partir de", "Product")
        : undefined;

    // Format VAE and VAD prices using formatPrice with shop context
    const vaePrice = productModel?.getPriceVae(true, cart);
    const vadPrice = productModel?.getPriceVad(true, cart);

    return (
      <LandingHighlighProductMargauxMotin
        variant={p("style_template")}
        title={p("title") || ""}
        subtitle={p("subtitle")}
        ctaLabel={isAddToCart ? getSubmitLabel(false, ctaLabel) : ctaLabel}
        description={p("description")}
        image={p("product_image")}
        video={mediaSource === "video" ? p("video_src") : undefined}
        animation={mediaSource === "animation" ? p("animation_src") : undefined}
        onClick={handleClick}
        backgroundImage={{
          desktop: p("background_media_desktop"),
          mobile: p("background_media_mobile"),
        }}
        imageLeft={p("image_position") === "image-left"}
        compositionLabel={p("composition") || ""}
        compositions={compositions}
        price={productModel?.formatPrice(undefined, undefined, undefined, 1, {
          auto_round_decimal: true,
          ignore_space: true,
        })}
        priceSubtitle={
          productModel && productModel.getQuantity() > 1
            ? productModel.displayPieces()
            : undefined
        }
        product_display_from_mention={displayFromMention}
        product_price_vae={vaePrice + "*"}
        product_price_vad={vadPrice + "*"}
        vaeLabel={t("À emporter", "Product")}
        vadLabel={t("En livraison", "Product")}
        hideCta={hideCta}
      />
    );
  });

export default HighlightProductMargauxMotin;
